/**
 * EmployerAccountPage.jsx — Employer workspace Account & Usage
 * surface (Prompt 3 — Commercial Surface Pass 1).
 *
 * Operationally honest. Read-only. Surfaces three operator-locked
 * tabs (RC ACCOUNT.TABS.A, 2026-05-27):
 *
 *   • Profile  — organisation identity (name, sector, stage, website,
 *                working model, location mode) + member-role badge.
 *   • Usage    — current-period evaluation / governance-pack /
 *                signals consumption (delegates to
 *                EmployerDashboardPanel — single source of truth).
 *   • Plans    — plan catalogue, billing posture, lifecycle banners
 *                (delegates to EmployerBillingPanel).
 *
 * Tab selection is URL-synced via `?tab=profile|usage|plans` so deep
 * links like `/employer/account?tab=plans` (used by the "View plans"
 * CTAs on the role candidates panel + bulk-eval confirm dialog)
 * land on the right pane without scrolling.
 *
 * STRICT — none of these surfaces shall appear:
 *   - Credits / Wallet / Top up / Buy more.
 *   - Stripe / invoice / receipt / overage rate.
 *   - Upgrade now / Cancel subscription / Self-serve activation.
 *   - Candidate billing surfaces.
 *
 * Data: /api/employer/v1/me (existing). NO new endpoints.
 * Writes: NONE.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Building2, ExternalLink, Loader2, ShieldAlert, Pencil, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { apiFetch } from '@/lib/api';
import { canManageOrg } from '@/lib/employerAuthority';
import EmployerDashboardPanel from '@/components/employer/EmployerDashboardPanel';
import EmployerBillingPanel from '@/components/employer/EmployerBillingPanel';


// Operator-locked tab identifiers. Order is the surfaced left-to-right
// order. Any value not in this list falls back to `profile`.
const TAB_VALUES = ['profile', 'usage', 'plans'];
const DEFAULT_TAB = 'profile';


function fieldOrDash(v) {
  if (v === null || v === undefined) return '\u2014';
  const s = String(v).trim();
  return s.length ? s : '\u2014';
}


export default function EmployerAccountPage() {
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [unmigrated, setUnmigrated] = useState(false);
  const [forbidden, setForbidden]   = useState(false);
  const [org, setOrg]               = useState(null);
  const [accountId, setAccountId]   = useState(null);
  const [memberRole, setMemberRole] = useState(null);

  // URL-synced tab selection. Hoisted above any early returns so the
  // rules-of-hooks invariant holds (hooks called unconditionally
  // every render). `?tab=plans` from the "View plans" CTAs
  // deep-links straight to the Plans pane without scrolling.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = (searchParams.get('tab') || '').toLowerCase();
  const activeTab = useMemo(
    () => (TAB_VALUES.includes(urlTab) ? urlTab : DEFAULT_TAB),
    [urlTab],
  );
  const onTabChange = useCallback((value) => {
    // Keep the URL clean — drop `?tab=` for the default pane so
    // direct hits on /employer/account don't carry a stale query.
    const next = new URLSearchParams(searchParams);
    if (value === DEFAULT_TAB) next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnmigrated(false);
    setForbidden(false);
    try {
      const data = await apiFetch('/api/employer/v1/me');
      const o = data?.organisation || null;
      setOrg(o);
      setAccountId(o?.id || data?.account_id || null);
      setMemberRole(data?.member?.role || null);
    } catch (err) {
      if (err?.status === 404) {
        setOrg(null);
      } else if (err?.status === 403) {
        setForbidden(true);
      } else if (err?.status === 503) {
        setUnmigrated(true);
      } else {
        setError(err?.message || 'Failed to load employer account.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-24"
        data-testid="employer-account-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-account-forbidden"
      >
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-xl font-semibold text-ndor-ink">Employer access only</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          Your account doesn&apos;t have employer workspace access.
          Ask an admin to grant it.
        </p>
      </div>
    );
  }

  if (unmigrated) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-account-unmigrated"
      >
        <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" />
        <h1 className="text-xl font-semibold text-ndor-ink">Setup pending</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          The employer organisations table hasn&apos;t been set up yet.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-account-error"
      >
        <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-rose-500" />
        <h1 className="text-xl font-semibold text-ndor-ink">Couldn&apos;t load account</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">{error}</p>
        <Button variant="outline" className="mt-6" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  // No organisation yet → send them to /employer to create it.
  if (!org) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-account-empty"
      >
        <Building2 className="mx-auto mb-4 h-10 w-10 text-ndor-ink-muted" />
        <h1 className="text-xl font-semibold text-ndor-ink">
          No organisation yet
        </h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          Create your organisation to see your account &amp; usage.
        </p>
        <Link to="/employer" data-testid="employer-account-create-cta">
          <Button className="mt-6 bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted">
            Set up organisation
          </Button>
        </Link>
      </div>
    );
  }

  // ── Loaded state ─────────────────────────────────────────────
  const culture = org?.culture_json || {};

  return (
    <div
      className="mx-auto max-w-3xl py-8 px-4 space-y-6"
      data-testid="employer-account-page"
    >
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-ndor-ink flex items-center gap-2">
          <Building2 className="h-5 w-5 text-ndor-ink-muted" />
          Account
        </h1>
        <p className="text-sm text-ndor-ink-muted">
          Your organisation, current-period usage, and plan posture.
        </p>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={onTabChange}
        className="w-full"
        data-testid="employer-account-tabs"
      >
        <TabsList
          className="grid grid-cols-3 w-full bg-ndor-surface-alt/60 border border-ndor-edge"
          data-testid="employer-account-tabs-list"
        >
          <TabsTrigger value="profile" data-testid="employer-account-tab-profile">
            Profile
          </TabsTrigger>
          <TabsTrigger value="usage" data-testid="employer-account-tab-usage">
            Usage
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="employer-account-tab-plans">
            Plans
          </TabsTrigger>
        </TabsList>

        {/* ── Profile tab ──────────────────────────────────────── */}
        <TabsContent
          value="profile"
          className="mt-6"
          data-testid="employer-account-tab-content-profile"
        >
          <Card
            className="border-ndor-edge shadow-sm"
            data-testid="employer-account-identity"
          >
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-ndor-ink">
                Organisation
              </CardTitle>
              {/* RC 1.61 P1.2 (operator-approved 2026-05-29): the
                  Edit-profile affordance is owner/admin-only (A1).
                  Backend RLS already blocks the underlying writes
                  for recruiters/reviewers — this gate just stops
                  non-owner members from clicking into a 4xx. The
                  doctrine matrix lives in lib/employerAuthority.js. */}
              {canManageOrg(memberRole) ? (
                <Link
                  to="/employer"
                  className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1"
                  data-testid="employer-account-edit-link"
                >
                  <Pencil className="h-3 w-3" />
                  Edit profile
                </Link>
              ) : null}
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div data-testid="employer-account-field-name">
                  <dt className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">Name</dt>
                  <dd className="text-ndor-ink">{fieldOrDash(org.name)}</dd>
                </div>
                <div data-testid="employer-account-field-sector">
                  <dt className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">Sector</dt>
                  <dd className="text-ndor-ink">{fieldOrDash(org.sector)}</dd>
                </div>
                <div data-testid="employer-account-field-size">
                  <dt className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">Stage</dt>
                  <dd className="text-ndor-ink">{fieldOrDash(org.size_band)}</dd>
                </div>
                <div data-testid="employer-account-field-website">
                  <dt className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">Website</dt>
                  <dd className="text-ndor-ink">
                    {org.website
                      ? (
                        <a
                          href={org.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
                        >
                          {org.website}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : '\u2014'}
                  </dd>
                </div>
                <div className="sm:col-span-2" data-testid="employer-account-field-working-model">
                  <dt className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">Working model</dt>
                  <dd className="text-ndor-ink">{fieldOrDash(culture.working_model)}</dd>
                </div>
                <div className="sm:col-span-2" data-testid="employer-account-field-location-mode">
                  <dt className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">Location mode</dt>
                  <dd className="text-ndor-ink">{fieldOrDash(culture.location_mode)}</dd>
                </div>
              </dl>
              <div className="mt-4 pt-3 border-t border-ndor-edge flex items-center gap-2 text-xs">
                <span className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">
                  Your role
                </span>
                <Badge
                  variant="outline"
                  data-testid="employer-account-my-role"
                >
                  {memberRole || '\u2014'}
                </Badge>
                <Link
                  to="/employer/help/permissions"
                  className="ml-auto text-[11px] text-ndor-ink-muted hover:text-ndor-ink underline-offset-2 hover:underline"
                  data-testid="employer-account-permissions-link"
                >
                  Roles &amp; permissions
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Usage tab ────────────────────────────────────────── */}
        <TabsContent
          value="usage"
          className="mt-6"
          data-testid="employer-account-tab-content-usage"
        >
          {/* EmployerDashboardPanel surfaces:
                • Identity block (You are: <role>)
                • Counters (active roles, open reviews, active reviewers, pending invites)
                • Plan chip strip (Base/Focus/Flow/Enterprise + caps)
                • Plan & Usage panel (active roles, signals, governance packs)
                • Honest placeholder footnote
              Mounted here so Account is the single source of truth. */}
          <EmployerDashboardPanel
            accountId={accountId}
            memberRole={memberRole}
          />
        </TabsContent>

        {/* ── Plans tab ────────────────────────────────────────── */}
        <TabsContent
          value="plans"
          className="mt-6"
          data-testid="employer-account-tab-content-plans"
        >
          {/* F.4.4 Employer Billing UI — canonical billing surface for
              the workspace. Lifecycle banners, plan selector, usage
              bars, Manage Billing CTA. Replaces the prior static
              "billing not active" footnote. The panel is internally
              flag-aware: while EMP_STRIPE_FEATURE_FLAG !== 'enabled'
              the mutation CTAs render but are disabled with
              explanatory copy. */}
          <EmployerBillingPanel accountId={accountId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
